import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function GameOver() {
  const navigate = useNavigate();
  const location = useLocation();
  const { gameData, sessionId } = location.state || {};

  const [breakdown, setBreakdown] = useState([]);
  const [playersFromApi, setPlayersFromApi] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (sessionId) {
      fetchGameBreakdown();
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const fetchGameBreakdown = async () => {
    try {
      const res = await fetch(`${window.API_URL}/api/scores/fun-stats/${sessionId}`);
      const data = await res.json();

      // Handle new format (with breakdown array) or old format (array of players)
      if (data.breakdown) {
        setBreakdown(data.breakdown);
        setPlayersFromApi(data.players || []);
      } else if (Array.isArray(data)) {
        // Old format - convert to new format for backwards compatibility
        setPlayersFromApi(data);
        setBreakdown([]);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching game breakdown:', error);
      setLoading(false);
    }
  };

  // Use API data if available, otherwise fall back to gameData
  const players = playersFromApi.length > 0 ? playersFromApi : (gameData?.players || []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <p className="text-white">Loading results...</p>
      </div>
    );
  }

  const winner = players[0];
  const second = players[1];
  const third = players[2];
  const rest = players.slice(3);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-3xl font-bold text-white mb-2">Game Over!</h1>
          <p className="text-slate-400">{gameData?.gameName || 'Game'} Complete</p>
        </div>

        {/* Podium */}
        <div className="flex justify-center items-end gap-4 mb-8">
          {/* Second Place */}
          {second && (
            <div className="flex flex-col items-center">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold mb-2 ring-4 ring-slate-400"
                style={{ backgroundColor: second.avatar_color || '#6366f1' }}
              >
                {second.name?.charAt(0).toUpperCase()}
              </div>
              <div className="text-slate-300 font-medium text-sm">{second.name?.split(' ')[0]}</div>
              <div className="text-slate-400 text-xs">{second.score} pts</div>
              <div className="w-20 h-24 bg-gradient-to-t from-slate-500 to-slate-400 rounded-t-lg mt-2 flex items-center justify-center">
                <span className="text-4xl">🥈</span>
              </div>
            </div>
          )}

          {/* First Place */}
          {winner && (
            <div className="flex flex-col items-center -mt-8">
              <div className="animate-bounce mb-2">
                <span className="text-4xl">👑</span>
              </div>
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold mb-2 ring-4 ring-yellow-400 shadow-lg shadow-yellow-400/50"
                style={{ backgroundColor: winner.avatar_color || '#6366f1' }}
              >
                {winner.name?.charAt(0).toUpperCase()}
              </div>
              <div className="text-white font-bold">{winner.name?.split(' ')[0]}</div>
              <div className="text-yellow-400 font-bold">{winner.score} pts</div>
              <div className="w-24 h-32 bg-gradient-to-t from-yellow-600 to-yellow-400 rounded-t-lg mt-2 flex items-center justify-center">
                <span className="text-5xl">🥇</span>
              </div>
            </div>
          )}

          {/* Third Place */}
          {third && (
            <div className="flex flex-col items-center">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold mb-2 ring-4 ring-orange-600"
                style={{ backgroundColor: third.avatar_color || '#6366f1' }}
              >
                {third.name?.charAt(0).toUpperCase()}
              </div>
              <div className="text-slate-300 font-medium text-sm">{third.name?.split(' ')[0]}</div>
              <div className="text-slate-400 text-xs">{third.score} pts</div>
              <div className="w-16 h-16 bg-gradient-to-t from-orange-700 to-orange-500 rounded-t-lg mt-2 flex items-center justify-center">
                <span className="text-3xl">🥉</span>
              </div>
            </div>
          )}
        </div>

        {/* Rest of players */}
        {rest.length > 0 && (
          <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
            <h2 className="text-sm font-semibold text-slate-400 mb-3">Also Played</h2>
            <div className="space-y-2">
              {rest.map((player, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-slate-400 font-mono w-6 text-center">{i + 4}</span>
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                      style={{ backgroundColor: player.avatar_color || '#6366f1' }}
                    >
                      {player.name?.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-white">{player.name}</span>
                  </div>
                  <span className="text-slate-400">{player.score} pts</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Game Breakdown */}
        {breakdown.length > 0 && (
          <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
            <h2 className="text-sm font-semibold text-white mb-4">📊 Game Breakdown</h2>
            <div className="space-y-3">
              {breakdown.map((stat, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-slate-700/50 rounded-lg">
                  <span className="text-2xl flex-shrink-0">{stat.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-amber-400 text-xs font-semibold uppercase tracking-wide">{stat.label}</p>
                    <p className="text-slate-200 text-sm">{stat.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* View Scorecard Button */}
        {sessionId && (
          <button
            onClick={() => navigate(`/history/${sessionId}`)}
            className="w-full py-4 rounded-xl font-bold text-lg bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-500 hover:to-teal-500 transition-all mb-3"
          >
            📋 View Full Scorecard
          </button>
        )}

        {/* Back to Dashboard */}
        <button
          onClick={() => navigate('/')}
          className="w-full py-4 rounded-xl font-bold text-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-500 hover:to-indigo-500 transition-all"
        >
          Back to Games
        </button>
      </div>
    </div>
  );
}
